import {
  Button,
  Divider,
  HStack,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  formatDate,
  Link,
  ModalConfirm,
  Select,
} from "@components/ui-kit";
import { select, Trans, t } from "@lingui/macro";
import {
  TimeTrackingItemOrderByColumnEnum,
  TimeTrackingReportGroupBy,
  type TimeTrackingReportItemFragment,
} from "@src/__generated__/graphql";
import { TimeBillingStatusTooltip } from "@src/components/modules/time-tracking/Report/Table/Col/time-billing-status-tooltip";
import { Icon } from "@src/components/ui-kit/Icon";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters/currency";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { useStore as useStores } from "@src/utils/hooks";
import { secondsToTime } from "@src/utils/time";
import { FieldState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { getBillingCategoryOptions } from "../../../projects/list/Store";
import MoveHoursToPositionModal from "../MoveHoursToPositionModal";
import MoveHoursToProjectModal from "../MoveHoursToProjectModal";
import { useStore } from "../Store";
import type { TimeTrackingRow } from "../Store/Store";
import Col from "./Col";

type TimeTrackingTableRow =
  | {
      id: string;
      rowType: "group";
      report: TimeTrackingRow;
      __subRows?: TimeTrackingTableRow[];
    }
  | {
      id: string;
      rowType: "item";
      item: TimeTrackingRow["__subRows"][0];
      parent: TimeTrackingRow;
    }
  | {
      id: string;
      rowType: "ungrouped-item";
      item: TimeTrackingReportItemFragment;
    };

const isGroupRow = (
  row: TimeTrackingTableRow,
): row is Extract<TimeTrackingTableRow, { rowType: "group" }> =>
  row.rowType === "group";

export const Table = observer(function Table() {
  const store = useStore();
  const { workspaceStore, taskDetailModalStore } = useStores();
  const [isMoveToProjectOpen, setIsMoveToProjectOpen] = useState(false);
  const [isMoveToPositionOpen, setIsMoveToPositionOpen] = useState(false);
  const [isChangeBillingCategoryOpen, setIsChangeBillingCategoryOpen] =
    useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const billingCategoryForm = useLocalObservable(() => ({
    billable: new FieldState("true"),
  }));

  const handleActionCompleted = () => {
    store.setSelectedIds([]);
    store.refetch();
  };

  const handleUpdateBillable = async (): Promise<void> => {
    const unlockedIds = await store.getUnlockedItemIds(store.selectedIds);

    if (unlockedIds.length === 0) {
      setIsChangeBillingCategoryOpen(false);
      return;
    }

    await store.updateTimeTrackingItemsBillable(
      billingCategoryForm.billable.$ === "true",
      unlockedIds,
    );
    setIsChangeBillingCategoryOpen(false);
  };

  const handleConfirmDeleteTimeEntries = async (): Promise<void> => {
    const unlockedIds = await store.getUnlockedItemIds(store.selectedIds);

    if (unlockedIds.length === 0) {
      setIsDeleteOpen(false);
      return;
    }

    await store.deleteTimeTrackingItems(unlockedIds);
  };

  const columns = useMemo<
    BodyColumnDef<TimeTrackingTableRow, TimeTrackingItemOrderByColumnEnum>[]
  >(() => {
    const cols: BodyColumnDef<
      TimeTrackingTableRow,
      TimeTrackingItemOrderByColumnEnum
    >[] = [
      {
        id: "tracked_for_date",
        meta: { label: t`Date` },
        header: () => t`Date`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Date,
        cell: ({ row }) =>
          isGroupRow(row.original) ? (
            <Col.GroupBy
              type={TimeTrackingReportGroupBy.Date}
              groupBy={row.original.report.groupBy ?? t`Work Expense`}
            />
          ) : (
            formatDate(new Date(row.original.item.tracked_for_date))
          ),
      },
      {
        id: "client_name",
        meta: { label: t`Brand (Client)` },
        header: () => t`Brand (Client)`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Brand,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            let groupBy: string | null | undefined;

            try {
              if (
                row.original.report.groupBy &&
                store.groupBy === TimeTrackingReportGroupBy.Date
              ) {
                groupBy = formatDate(new Date(row.original.report.groupBy));
              } else {
                groupBy = row.original.report.groupBy;
              }
            } catch {
              groupBy = row.original.report.groupBy;
            }

            return (
              <Col.GroupBy
                type={TimeTrackingReportGroupBy.Brand}
                groupBy={groupBy}
              />
            );
          }

          return (
            <Link
              isExternal
              href={`/brands/detail?id=${row.original.item.brand_id}`}
            >{`${row.original.item.brand_name} (${row.original.item.client_name})`}</Link>
          );
        },
      },
      {
        id: "project_title",
        meta: { label: t`Project` },
        header: () => t`Project`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Project,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <Col.ProjectTitle
              project_code={row.original.item.project_code}
              project_title={row.original.item.project_title}
              project_id={row.original.item.project_id}
            />
          ),
      },
      {
        id: "our_work_budget_item_title",
        meta: { label: t`Budget item` },
        header: () => t`Budget item`,
        orderBy: TimeTrackingItemOrderByColumnEnum.OurWorkBudgetItem,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.item.our_work_budget_item_title,
      },
      {
        id: "time_tracking_work_type_title",
        meta: { label: t`Position` },
        header: () => t`Position`,
        orderBy: TimeTrackingItemOrderByColumnEnum.WorkType,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.item.time_tracking_work_type_title,
      },
      {
        id: "task_title",
        meta: { label: t`Task` },
        header: () => t`Task`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Task,
        cell: ({ row }) => {
          const original = row.original;

          if (isGroupRow(original)) {
            return null;
          }

          const { item } = original;

          return (
            <Button
              onClick={() => {
                taskDetailModalStore.disclosureState.onOpen({
                  id: item.task_id,
                });
              }}
              variant="link"
            >
              {item.task_name}
            </Button>
          );
        },
      },
      {
        id: "team_name",
        meta: { label: t`Team` },
        header: () => t`Team`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Team,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : row.original.item.team_name,
      },
      {
        id: "user_full_name",
        meta: { label: t`User` },
        header: () => t`User`,
        orderBy: TimeTrackingItemOrderByColumnEnum.User,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : (row.original.item.user_full_name ?? <Trans>Work Expense</Trans>),
      },
      {
        id: "invoiced",
        meta: { label: t`Invoiced` },
        header: () => t`Invoiced`,
        orderBy: TimeTrackingItemOrderByColumnEnum.InvoicedTime,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <TimeBillingStatusTooltip
              type="invoiced"
              item={row.original.item}
            />
          ),
      },
      {
        id: "overserviced",
        meta: { label: t`Overserviced` },
        header: () => t`Overserviced`,
        orderBy: TimeTrackingItemOrderByColumnEnum.OverservicedTime,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <TimeBillingStatusTooltip
              type="overserviced"
              item={row.original.item}
            />
          ),
      },
      {
        id: "uninvoiced",
        meta: { label: t`Uninvoiced` },
        header: () => t`Uninvoiced`,
        orderBy: TimeTrackingItemOrderByColumnEnum.UninvoicedTime,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <TimeBillingStatusTooltip
              type="uninvoiced"
              item={row.original.item}
            />
          ),
      },
      {
        id: "tracked_time",
        meta: {
          label: String(
            workspaceStore.settings?.timeTrackingRateTypeReadable() ?? "",
          ),
        },
        header: () => workspaceStore.settings?.timeTrackingRateTypeReadable(),
        orderBy: TimeTrackingItemOrderByColumnEnum.TrackedHours,
        cell: ({ row }) =>
          isGroupRow(row.original) ? (
            <p className="font-semibold">
              {workspaceStore.settings?.convertTimeTrackingRate(
                row.original.report.trackedTime,
              )}
            </p>
          ) : (
            workspaceStore.settings?.convertTimeTrackingRate(
              row.original.item.tracked_time,
            )
          ),
      },
      {
        id: "hourly_rate",
        meta: { label: t`Hourly rate` },
        header: () => t`Hourly rate`,
        orderBy: TimeTrackingItemOrderByColumnEnum.HourlyRate,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) return null;
          const rate = row.original.item.hourly_rate;
          if (!rate) return <Text>-</Text>;
          return <Text>{currency.formatByWorkspace(rate)}</Text>;
        },
      },
      {
        id: "tracked_amount",
        meta: { label: t`Tracked amount` },
        header: () => t`Tracked amount`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Amount,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) return null;
          if (typeof row.original.item.amount !== "number") {
            return <Text>-</Text>;
          }
          return (
            <Text>{currency.formatByWorkspace(row.original.item.amount)}</Text>
          );
        },
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: () => t`Note`,
        orderBy: TimeTrackingItemOrderByColumnEnum.Note,
        cell: ({ row }) => {
          if (isGroupRow(row.original) || !row.original.item.note) {
            return null;
          }

          return (
            <Tooltip
              hasArrow
              label={
                <TextEditor
                  isEditable={false}
                  initialValue={row.original.item.note}
                  attachments={[]}
                  onChange={() => {}}
                />
              }
              placement="top"
              shouldWrapChildren
            >
              <TextEditor
                isEditable={false}
                initialValue={row.original.item.note}
                attachments={[]}
                onChange={() => {}}
                noOfLines={1}
              />
            </Tooltip>
          );
        },
      },
      {
        id: "locked_at",
        meta: { label: t`Is locked` },
        header: () => t`Is locked`,
        orderBy: TimeTrackingItemOrderByColumnEnum.LockedAt,
        cell: ({ row }) => {
          if (isGroupRow(row.original) || !row.original.item.locked_at) {
            return null;
          }

          return <Icon name="lock-01" />;
        },
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: "" },
        header: () => "",
        cell: () => null,
      },
    ];

    return cols.filter((column) => {
      if (
        column.id === "tracked_for_date" &&
        store.groupBy === TimeTrackingReportGroupBy.Date
      ) {
        return false;
      }

      if (
        column.id === "client_name" &&
        store.groupBy === TimeTrackingReportGroupBy.Brand
      ) {
        return false;
      }

      if (
        column.id === "project_title" &&
        store.groupBy === TimeTrackingReportGroupBy.Project
      ) {
        return false;
      }

      if (
        column.id === "our_work_budget_item_title" &&
        store.groupBy === TimeTrackingReportGroupBy.BudgetItem
      ) {
        return false;
      }

      if (
        column.id === "time_tracking_work_type_title" &&
        store.groupBy === TimeTrackingReportGroupBy.WorkType
      ) {
        return false;
      }

      if (
        column.id === "team_name" &&
        store.groupBy === TimeTrackingReportGroupBy.Task
      ) {
        return false;
      }

      if (
        column.id === "user_full_name" &&
        store.groupBy === TimeTrackingReportGroupBy.User
      ) {
        return false;
      }

      if (
        ["invoiced", "overserviced", "uninvoiced"].includes(
          column.id as string,
        ) &&
        !workspaceStore.settings?.moduleSettings.time_billing
      ) {
        return false;
      }

      if (
        ["hourly_rate", "tracked_amount"].includes(column.id as string) &&
        !workspaceStore.settings?.show_amounts_in_time_tracking_report
      ) {
        return false;
      }

      return true;
    });
  }, [
    store.groupBy,
    taskDetailModalStore.disclosureState,
    workspaceStore.settings,
  ]);

  const data = useMemo<TimeTrackingTableRow[]>(() => {
    if (store.isGroupedByNone) {
      return store.ungroupedItems.map((item) => ({
        id: item.id,
        rowType: "ungrouped-item",
        item: item,
      }));
    }

    return store.groupedReports.map((report) => ({
      id: report.id,
      rowType: "group",
      report,
      __subRows: report.__subRows.map((item) => ({
        id: item.id,
        rowType: "item",
        item,
        parent: report,
      })),
    }));
  }, [store.groupedReports, store.isGroupedByNone, store.ungroupedItems]);

  const bulkActions = useMemo<BulkAction[]>(
    () => [
      {
        id: "time-tracking-report-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () =>
          store.isDeleteLoading ||
          store.isUnlockLoading ||
          store.isUpdateBillableLoading ||
          store.isLockCheckLoading,
        subActions: [
          {
            id: "move-to-project",
            label: () =>
              select(String(store.selectedIds.length), {
                "0": `Move all time entries to another project`,
                other: `Move selected time entries to another project`,
              }),
            callback: () => {
              setIsMoveToProjectOpen(true);
            },
            isDisabled: () => store.hasLockedSelected,
            tooltip: t`You can't edit or move time entries that are already allocated to locked Time Billing calculation.`,
          },
          {
            id: "move-to-position",
            label: () =>
              select(String(store.selectedIds.length), {
                "0": `Move all time entries to another position`,
                other: `Move selected time entries to another position`,
              }),
            callback: () => {
              setIsMoveToPositionOpen(true);
            },
            isDisabled: () => store.hasLockedSelected,
            tooltip: t`You can't edit or move time entries that are already allocated to locked Time Billing calculation.`,
          },
          {
            id: "change-billing-category",
            label: () => t`Change time entries Billing Category`,
            callback: () => {
              setIsChangeBillingCategoryOpen(true);
            },
            isDisabled: () => store.hasLockedSelected,
            tooltip: t`You can't edit or move time entries that are already allocated to locked Time Billing calculation.`,
          },
          ...(can("project_billingTime_lock")
            ? [
                {
                  id: "unlock-time-entries",
                  label: () => t`Unlock time entries`,
                  callback: (selectedIds: string[] | Set<string>) => {
                    store.unlockTimeTrackingItems(Array.from(selectedIds));
                  },
                },
              ]
            : []),
          {
            id: "delete-time-entries",
            label: () => t`Delete time entries`,
            isDestructive: true,
            callback: () => {
              setIsDeleteOpen(true);
            },
            isDisabled: () => store.hasLockedSelected,
            tooltip: t`You can't edit or move time entries that are already allocated to locked Time Billing calculation.`,
          },
        ],
      },
    ],
    [store],
  );

  return (
    <>
      <DataTable.Body
        tableKey="time-tracking-report"
        data={data}
        columns={columns}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={store.handleOrderChange}
        selected={store.selectedIds}
        onSelect={store.setSelectedIds}
        bulkActions={bulkActions}
        allIds={store.summary?.ids}
        enableRowSelection
        enableSubRowSelection={!store.isGroupedByNone}
        rowSelectionMode={store.isGroupedByNone ? "rows" : "subrows"}
        bulkActionsInfoBlock={
          <HStack
            fontSize="xs"
            divider={<Divider h="6" orientation="vertical" />}
            spacing="4"
          >
            <VStack align="end" spacing="0">
              <Text fontSize="inherit" fontWeight="semibold">
                <Trans>Total Tracked</Trans>
              </Text>

              <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                <Text fontSize="inherit" textAlign="end">
                  {store.selectedSummary
                    ? secondsToTime(store.selectedSummary.tracked_time)
                    : "-"}
                </Text>
              </Skeleton>

              {workspaceStore.settings
                ?.show_amounts_in_time_tracking_report && (
                <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                  <Text fontSize="inherit" textAlign="end">
                    {store.selectedSummary?.amount
                      ? currency.formatByWorkspace(store.selectedSummary.amount)
                      : "-"}
                  </Text>
                </Skeleton>
              )}
            </VStack>

            {workspaceStore.settings?.moduleSettings.time_billing && (
              <VStack align="end" spacing="0">
                <Text fontSize="inherit" fontWeight="semibold">
                  <Trans>Total Invoiced</Trans>
                </Text>

                <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                  <Text fontSize="inherit" textAlign="end">
                    {store.selectedSummary?.invoiced_seconds
                      ? secondsToTime(store.selectedSummary.invoiced_seconds)
                      : "-"}
                  </Text>
                </Skeleton>

                {workspaceStore.settings
                  ?.show_amounts_in_time_tracking_report && (
                  <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                    <Text fontSize="inherit" textAlign="end">
                      {store.selectedSummary?.invoiced_amount
                        ? currency.formatByWorkspace(
                            store.selectedSummary.invoiced_amount,
                          )
                        : "-"}
                    </Text>
                  </Skeleton>
                )}
              </VStack>
            )}

            {workspaceStore.settings?.moduleSettings.time_billing && (
              <VStack align="end" spacing="0">
                <Text fontSize="inherit" fontWeight="semibold">
                  <Trans>Total Overserviced</Trans>
                </Text>

                <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                  <Text fontSize="inherit" textAlign="end">
                    {store.selectedSummary?.overserviced_seconds
                      ? secondsToTime(
                          store.selectedSummary.overserviced_seconds,
                        )
                      : "-"}
                  </Text>
                </Skeleton>

                {workspaceStore.settings
                  ?.show_amounts_in_time_tracking_report && (
                  <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                    <Text fontSize="inherit" textAlign="end">
                      {store.selectedSummary?.overserviced_amount
                        ? currency.formatByWorkspace(
                            store.selectedSummary.overserviced_amount,
                          )
                        : "-"}
                    </Text>
                  </Skeleton>
                )}
              </VStack>
            )}

            {workspaceStore.settings?.moduleSettings.time_billing && (
              <VStack align="end" spacing="0">
                <Text fontSize="inherit" fontWeight="semibold">
                  <Trans>Total Uninvoiced</Trans>
                </Text>

                <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                  <Text fontSize="inherit" textAlign="end">
                    {store.selectedSummary?.uninvoiced_seconds
                      ? secondsToTime(store.selectedSummary.uninvoiced_seconds)
                      : "-"}
                  </Text>
                </Skeleton>

                {workspaceStore.settings
                  ?.show_amounts_in_time_tracking_report && (
                  <Skeleton minW="40px" isLoaded={!store.isSummaryLoading}>
                    <Text fontSize="inherit" textAlign="end">
                      {store.selectedSummary?.uninvoiced_amount
                        ? currency.formatByWorkspace(
                            store.selectedSummary.uninvoiced_amount,
                          )
                        : "-"}
                    </Text>
                  </Skeleton>
                )}
              </VStack>
            )}
          </HStack>
        }
      />

      <MoveHoursToProjectModal
        selectedIds={store.selectedIds}
        clientsSimpleMap={store.optionsData?.clientsSimpleMap ?? []}
        onMoveCompleted={handleActionCompleted}
        isOpen={isMoveToProjectOpen}
        onClose={() => setIsMoveToProjectOpen(false)}
      />

      <MoveHoursToPositionModal
        itemIds={store.selectedIds}
        onMoveCompleted={handleActionCompleted}
        isOpen={isMoveToPositionOpen}
        onClose={() => setIsMoveToPositionOpen(false)}
      />

      <ModalConfirm
        heading={t`Change time entries Billing Category`}
        isOpen={isChangeBillingCategoryOpen}
        onClose={() => setIsChangeBillingCategoryOpen(false)}
        onConfirm={handleUpdateBillable}
        isLoading={store.isUpdateBillableLoading}
      >
        <Select
          {...fieldToSelectProps(
            billingCategoryForm.billable,
            getBillingCategoryOptions(),
          )}
        />
      </ModalConfirm>

      <ModalConfirm
        heading={t`Delete time entries`}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDeleteTimeEntries}
        isLoading={store.isDeleteLoading}
        destructive
      >
        <Trans>
          Are you sure you want to delete the selected time entries? This action
          cannot be undone.
        </Trans>
      </ModalConfirm>
    </>
  );
});
